import { Header } from "@repo/ui/header";
import { useTranslations } from "next-intl";
import React from "react";
import { ProductDetail } from "@repo/ui/productDetail";
import type { Products as ProductsType } from "@repo/fetch-repository/products";
import { ProductsRepository } from "@repo/fetch-repository/products";
import type {
  ErrorType,
  SeoType,
  BreadCrumb,
} from "@repo/fetch-repository/factory";
import type { Services as ServicesType } from "@repo/fetch-repository/services";
import { Banner } from "@repo/ui/banner";
import { ServicesBenefit } from "@repo/ui/serviceBenefit";
import { WorkWithUs } from "@repo/ui/workWithUsForm";
import Head from "next/head";
import type { Separator as SeparatorType } from "@repo/fetch-repository/settings";
import { Separator } from "@repo/ui/separator";
import {
  RelatedServicesRepository,
  type Service as RelatedServicesType,
} from "@repo/fetch-repository/productsRelatedServices";
import {
  RelatedProjectsRepository,
  type Projects as RelatedProjectsType,
} from "@repo/fetch-repository/productsRelatedProjects";
import { ProjectCarousel } from "@repo/ui/projectCarousel";
// import { CatalogCarousel } from "@repo/ui/catalogCarousel";

export async function getServerSideProps(context: {
  params: { id: number[] | string[] };
  locale: string;
}): Promise<{
  props: {
    productData: ProductsType | null;
    productError: ErrorType | null;
    relatedServicesData: RelatedServicesType[] | null;
    relatedServicesError: ErrorType | null;
    relatedProjectsData: RelatedProjectsType[] | null;
    relatedProjectsError: ErrorType | null;
    messages: Record<string, string>;
    seo: SeoType | null;
    separator: SeparatorType | null;
    breadCrumbs: BreadCrumb[] | null;
  };
  redirect?: {
    destination: string;
    permanent: boolean;
  };
}> {
  const productRepository: ProductsRepository = new ProductsRepository(
    context.locale
  );
  const relatedServices: RelatedServicesRepository =
    new RelatedServicesRepository(context.locale);

  const relatedProjects: RelatedProjectsRepository =
    new RelatedProjectsRepository(context.locale);
  const res = await Promise.all([
    productRepository.get(Number(context.params.id[0]), {
      with: "video,gallery_images",
    }),
    relatedServices.getRelatedServices(Number(context.params.id[0]), {
      per_page: 15,
    }),
    relatedProjects.getRelatedProjects(Number(context.params.id[0]), {
      per_page: 15,
      with: "region,industrial_process,project_type,rectifier_technology,customer",
    }),
  ]);

  let productData = null;
  let breadCrumbs = null;
  let productError: ErrorType | null = null;
  let seo: SeoType | null = null;
  if (res[0].succeeded) {
    productData = res[0].data;
    breadCrumbs = res[0].breadCrumbs;
    seo = res[0].seo;
  } else {
    console.error(res[0].errors);
    productError = {
      message: res[0].errors?.message || null,
      status: res[0].errors?.status || null,
    };
  }
  if (context.params.id.length === 1) {
    return {
      redirect: {
        destination: `/${context.locale}/products/${context.params.id[0]}/${encodeURIComponent(
          productData?.name.replace(/\s+/g, "-").toLowerCase() || ""
        )}`,
        permanent: true,
      },
      props: {
        productData: null,
        relatedServicesData: null,
        relatedServicesError: null,
        relatedProjectsData: null,
        relatedProjectsError: null,
        productError: null,
        messages: {},
        seo: null,
        separator: null,
        breadCrumbs,
      },
    };
  }

  let relatedServicesData = null;
  let relatedServicesError: ErrorType | null = null;
  if (res[1].succeeded) {
    relatedServicesData = res[1].data;
  } else {
    relatedServicesError = {
      message: res[1].errors?.message || null,
      status: res[1].errors?.status || null,
    };
  }

  let relatedProjectsData = null;
  let relatedProjectsError: ErrorType | null = null;
  if (res[2].succeeded) {
    relatedProjectsData = res[2].data;
  } else {
    relatedProjectsError = {
      message: res[2].errors?.message || null,
      status: res[2].errors?.status || null,
    };
  }

  let separator: SeparatorType | null = null;
  if (productData?.separators) {
    separator = {
      type: productData.separators.type,
      cover: productData.separators.cover_id,
      catalogs: productData.separators.catalog_ids?.data || null,
      title: productData.separators.title || null,
      description: productData.separators.description || null,
    };
  }
  const messages = (await import(`../../locales/${context.locale}.json`)) as {
    default: Record<string, string>;
  };

  return {
    props: {
      messages: messages.default,
      productData,
      productError,
      relatedProjectsData,
      relatedProjectsError,
      relatedServicesData,
      relatedServicesError,
      seo,
      separator,
      breadCrumbs,
    },
  };
}

export default function Product(props: {
  productData: ProductsType | null;
  productError: ErrorType | null;
  servicesData: ServicesType[] | null;
  servicesError: ErrorType | null;
  seo: SeoType | null;
  relatedServicesData: RelatedServicesType[] | null;
  relatedServicesError: ErrorType | null;
  relatedProjectsData: RelatedProjectsType[] | null;
  relatedProjectsError: ErrorType | null;
  separator: SeparatorType | null;
  breadCrumbs: BreadCrumb[] | null;
}): React.JSX.Element {
  const t = useTranslations();

  const renderContent = (): React.JSX.Element | null => {
    if (props.productError) {
      return (
        <div className="web-font-helvetica web-flex web-items-center web-justify-center web-flex-col lg:web-gap-7 lg:web-py-36">
          <h1 className="web-text-9xl web-font-bold">
            {props.productError.status}
          </h1>
          <p className="web-text-4xl">{props.productError.message}</p>
        </div>
      );
    }

    if (props.productData) {
      return (
        <div className="web-flex web-flex-col">
          <div className="web-container web-relative web-mx-auto web-py-12 md:web-py-28 lg:web-py-32">
            <div className="web-absolute ltr:web-left-10 rtl:web-right-10 web-top-[30%] lg:web-flex web-hidden">
              <div className="web-size-full web-relative web-flex web-items-center web-justify-center">
                <div className="web-absolute rtl:web-rotate-90 ltr:-web-rotate-90 web-font-thin lg:web-text-3xl web-text-xl web-px-2 web-text-gray-300 before:web-content-[''] before:web-inline-block before:web-bg-gray-300 before:web-h-px before:lg:web-w-14 before:web-w-10 after:web-content-[''] after:web-inline-block after:web-bg-gray-300 after:web-h-px after:lg:web-w-14 after:web-w-10 web-flex web-items-center">
                  <span className="web-mx-2 web-uppercase web-whitespace-nowrap rtl:-web-mt-3 rtl:web-font-doran rtl:web-font-medium">
                    {t("productDetails.header.title")}
                  </span>
                </div>
              </div>
            </div>
            <ProductDetail
              detail={props.productData.detail}
              gallery_images={props.productData.gallery_images.data}
              id={props.productData.id}
              image={
                !Array.isArray(props.productData.main_image.data)
                  ? props.productData.main_image.data.url
                  : undefined
              }
              title={props.productData.name}
              video={props.productData.video}
            />
          </div>
          {props.separator ? (
            <Separator
              banner={{ marginBottom: "-7%", marginTop: "0%" }}
              separator={props.separator}
            />
          ) : null}
          {/* <div className="web-container web-relative web-mx-auto web-py-16 md:web-py-28 lg:web-py-32">
            <CatalogCarousel title={t("catalogsCarouselWithBanner.title")} />
          </div> */}
          {props.relatedProjectsData?.length ? (
            <div className=" web-py-16 md:web-py-28 lg:web-py-32">
              <ProjectCarousel projects={props.relatedProjectsData} />
            </div>
          ) : null}
          {props.relatedServicesData?.length ? (
            <div className="web-container web-relative web-mx-auto web-py-16 md:web-py-28 lg:web-py-32">
              <ServicesBenefit
                services={props.relatedServicesData}
                title={t("servicesBenefit.title")}
              />
            </div>
          ) : null}
          <div>
            <Banner
              backgroundImage="/images/banner-worker-protective.png"
              marginBottom="-27%"
            >
              <WorkWithUs
                descriptionLabel={t("workWithUs.descriptionLabel")}
                emailLabel={t("workWithUs.emailLabel")}
                fileLabel={t("workWithUs.fileLabel")}
                firstNameLabel={t("workWithUs.firstNameLabel")}
                formTitle={t("workWithUs.formTitle")}
                lastNameLabel={t("workWithUs.lastNameLabel")}
                mobileLabel={t("workWithUs.mobileLabel")}
                resetButtonText={t("form.reset")}
                submitButtonText={t("form.submit")}
                toastErrorMessage={t("workWithUs.toastErrorMessage")}
                toastErrorTitle={t("workWithUs.toastErrorTitle")}
                toastSuccessMessage={t("workWithUs.toastSuccessMessage")}
                toastSuccessTitle={t("workWithUs.toastSuccessTitle")}
                validationMessageEmail={t("validation.email")}
                validationMessageFileSize={t("validation.fileSize")}
                validationMessageInvalidFile={t("validation.invalidFile")}
                validationMessageInvalidTypeFile={t(
                  "validation.invalidTypeFile"
                )}
                validationMessageOneFileAllowed={t("validation.oneFileAllowed")}
                validationMessageRequired={t("validation.required")}
              />
            </Banner>
          </div>
        </div>
      );
    }

    return (
      <h5 className="web-py-40 web-container web-font-helvetica font-[100] rtl:web-font-doran rtl:web-font-bold lg:web-text-5xl web-text-sm md:web-text-3xl web-uppercase web-text-center">
        {t("productNotFound")}
      </h5>
    );
  };

  return (
    <>
      <Head>
        <title>{props.seo?.data.title || props.productData?.name}</title>
        <meta content={props.seo?.data.description || ""} name="description" />
        <meta content={props.seo?.data.robots || ""} name="robots" />
        <meta content={props.seo?.data.author || ""} name="author" />
      </Head>
      <Header
        backgroundImage={
          !Array.isArray(props.productData?.cover.data)
            ? props.productData?.cover.data.url || "/images/header-bg.png"
            : "/images/header-bg.png"
        }
        breadCrumbs={props.breadCrumbs}
        description={`${t("productDetails.header.description")} > ${
          props.productData?.name
        }`}
        subTitle={t("productDetails.header.subtitle")}
        title={props.productData?.name || t("productDetails.header.title")}
      />
      {renderContent()}
    </>
  );
}
